import React, { useEffect, useMemo, useRef, useState } from "react";
// Only financially up-to-date members may be used as a referee/mentor, so the
// picker searches the up-to-date-only endpoint (aliased to keep the call sites).
import { useLazySearchUpToDateMembersByQQuery as useLazySearchMembersByQQuery } from "../../api/probationerApplicationApi";
import {
  Users,
  Search,
  UserPlus,
  ArrowLeft,
  ArrowRight,
  AlertCircle,
  X,
  CheckCircle2,
  Mail,
  Phone,
  Fingerprint,
  RotateCcw,
  Loader2,
} from "lucide-react";

export type SelectedMember = {
  id: string | number; // numeric users.id — used only for duplicate-check keying
  member_id: string; // UUID from members table — sent to backend
  surname?: string;
  other_names?: string;
  email?: string;
  phone?: string;
  membership_no?: string;
};

type RefRow = { member?: SelectedMember; relationship: string };

export type Step7RefereeInitial = {
  referees: RefRow[];
};

function fullName(m?: SelectedMember) {
  if (!m) return "—";
  return [m.surname, m.other_names].filter(Boolean).join(" ");
}

export default function Step7Referees({
  onSubmit,
  busy,
  onBack,
  initialValues,
  readOnly,
}: {
  busy: boolean;
  onBack: () => void;
  readOnly?: boolean;
  initialValues?: Step7RefereeInitial | null;
  onSubmit: (
    referees: { member_id: string; relationship: string }[],
  ) => Promise<void>;
}) {
  const [rows, setRows] = useState<RefRow[]>([
    { relationship: "" },
    { relationship: "" },
  ]);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!initialValues || hydratedRef.current) return;
    const incoming = Array.isArray(initialValues.referees)
      ? initialValues.referees
      : [];
    if (incoming.length === 0) return;
    hydratedRef.current = true;

    const a = incoming[0] || { relationship: "" };
    const b = incoming[1] || { relationship: "" };
    setRows([
      { member: a.member, relationship: a.relationship ?? "" },
      { member: b.member, relationship: b.relationship ?? "" },
    ]);
  }, [initialValues]);

  const [formError, setFormError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);

  const [triggerSearch, searchState] = useLazySearchMembersByQQuery();

  useEffect(() => {
    if (activeRowIndex === null) return;
    const qq = q.trim();
    if (qq.length < 2) return;

    const t = window.setTimeout(() => triggerSearch(qq), 350);
    return () => window.clearTimeout(t);
  }, [q, activeRowIndex, triggerSearch]);

  const results = useMemo(() => {
    return ((searchState.data as any)?.data || []) as SelectedMember[];
  }, [searchState.data]);

  const setRow = (i: number, patch: Partial<RefRow>) => {
    setRows((s) => s.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

    const RELATIONSHIP_OPTIONS = ["Employer", "Mentor", "Sponsor"];

  const selectedIds = useMemo(
    () => rows.map((r) => r.member?.member_id).filter(Boolean) as string[],
    [rows],
  );

  const canSubmit = useMemo(() => {
    return (
      rows[0].member?.member_id &&
      rows[1].member?.member_id &&
      rows[0].member.member_id !== rows[1].member.member_id &&
      rows[0].relationship.trim() &&
      rows[1].relationship.trim()
    );
  }, [rows]);

  const selectMemberForRow = (rowIndex: number, m: SelectedMember) => {
    if (selectedIds.includes(m.member_id)) {
      setFormError("This member is already selected as a referee.");
      return;
    }
    setFormError(null);
    setRow(rowIndex, { member: m });
    setActiveRowIndex(null);
    setQ("");
  };

  return (
    <form
      className="space-y-8 pb-10"
      onSubmit={async (e) => {
        e.preventDefault();
        setFormError(null);
        if (!canSubmit) {
          setFormError(
            "Please ensure both referees are selected and different.",
          );
          return;
        }
        const payload = rows.map((r) => ({
          member_id: r.member?.member_id || "",
          relationship: r.relationship,
        }));
        await onSubmit(payload);
      }}
    >
      {/* Header */}
      <div className="border-b border-gray-100 pb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-900 rounded-xl">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Referees</h2>
            <p className="text-sm text-gray-500">
              Search and select two different members to act as your referees.
            </p>
          </div>
        </div>
      </div>

      {formError && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl text-red-700 text-sm font-medium animate-in fade-in slide-in-from-top-1">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {formError}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {rows.map((r, i) => (
          <div
            key={i}
            className={`relative p-6 rounded-3xl border-2 transition-all ${
              r.member
                ? "bg-white border-gray-200"
                : "bg-gray-50 border-dashed border-gray-300"
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                Slot 0{i + 1}
              </span>
              {r.member && (
                <button
                  type="button"
                  onClick={() => setRow(i, { member: undefined })}
                  className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-all"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
            </div>

            {r.member ? (
              <div className="space-y-4 animate-in zoom-in-95 duration-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-900 rounded-2xl flex items-center justify-center text-white font-bold text-lg">
                    {r.member.surname?.[0]}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 leading-tight">
                      {fullName(r.member)}
                    </h4>
                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      <Fingerprint className="w-3 h-3" />{" "}
                      {r.member?.membership_no}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 border-t border-gray-50 pt-3">
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Mail className="w-3 h-3" /> {r.member.email || "No Email"}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Phone className="w-3 h-3" /> {r.member.phone || "No Phone"}
                  </div>
                </div>

                <div className="pt-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">
                    Relationship
                  </label>
                  <select
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-100 outline-none transition-all"
                    value={r.relationship}
                    onChange={(e) =>
                      setRow(i, { relationship: e.target.value })
                    }
                    disabled={busy}
                  >
                    <option value="">Select relationship</option>
                    {RELATIONSHIP_OPTIONS.filter(
                      (opt) => opt !== rows[1 - i]?.relationship
                    ).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              <div className="py-10 flex flex-col items-center justify-center text-center space-y-4">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-gray-100">
                  <UserPlus className="w-6 h-6 text-gray-300" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-500">
                    No Referee Selected
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveRowIndex(i)}
                    className="mt-2 text-xs font-bold text-gray-900 underline underline-offset-4 hover:text-gray-600"
                  >
                    Click to Search
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Search Overlay/Panel */}
      {activeRowIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white w-full max-w-lg rounded-[32px] shadow-2xl border border-gray-100 overflow-hidden animate-in slide-in-from-bottom-4">
            <div className="p-6 border-b border-gray-50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">
                  Select Referee {activeRowIndex + 1}
                </h3>
                <button
                  onClick={() => setActiveRowIndex(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-all"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  autoFocus
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-gray-900/5 transition-all"
                  placeholder="Search by name, email or Membership No..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
            </div>

            <div className="max-h-[350px] overflow-y-auto p-4 space-y-2">
              {searchState.isFetching ? (
                <div className="py-10 flex flex-col items-center gap-2 text-gray-400">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span className="text-xs font-medium">
                    Searching member database...
                  </span>
                </div>
              ) : q.trim().length < 2 ? (
                <div className="py-10 text-center text-xs font-bold text-gray-300 uppercase tracking-widest italic">
                  Start typing to search
                </div>
              ) : results.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-400">
                  No matching members found.
                </div>
              ) : (
                results.map((m) => {
                  const isAlreadyPicked = selectedIds.includes(m.member_id);
                  return (
                    <button
                      key={m.member_id}
                      type="button"
                      disabled={isAlreadyPicked}
                      onClick={() => selectMemberForRow(activeRowIndex, m)}
                      className={`w-full text-left p-4 rounded-2xl border transition-all flex items-center justify-between group
                        ${
                          isAlreadyPicked
                            ? "bg-gray-50 border-gray-100 opacity-50 cursor-not-allowed"
                            : "bg-white border-gray-100 hover:border-gray-900 hover:shadow-md"
                        }`}
                    >
                      <div>
                        <div className="font-bold text-gray-900 group-hover:text-gray-900 transition-colors">
                          {fullName(m)}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {m.membership_no && (
                            <span className="font-medium">
                              {m.membership_no} •{" "}
                            </span>
                          )}
                          {m.email}
                        </div>
                      </div>
                      {!isAlreadyPicked && (
                        <CheckCircle2 className="w-5 h-5 text-gray-200 group-hover:text-gray-900 transition-colors" />
                      )}
                      {isAlreadyPicked && (
                        <AlertCircle className="w-5 h-5 text-gray-300" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {!readOnly && (
        <div className="flex justify-between items-center pt-8 border-t border-gray-100">
          <button
            type="button"
            disabled={busy}
            onClick={onBack}
            className="flex items-center gap-2 px-6 py-3 border border-gray-200 rounded-xl font-bold text-sm text-gray-600 hover:bg-gray-50 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <button
            disabled={busy || !canSubmit}
            className="group flex items-center gap-3 px-8 py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg shadow-gray-200"
          >
            {busy ? "Processing..." : "Save & Continue"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      )}
    </form>
  );
}
